<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import PlusIcon from '@/assets/icons/plus.svg';
import 'vue-select/dist/vue-select.css';

const store = useStore();

const model = defineModel({ type: Array, default: [] });

const { user, isOrganization } = defineProps({
  user: {
    type: Object,
    required: true,
  },
  isOrganization: Boolean,
});

const labelsFromSearch = ref([]);

const searchLabels = async (search, loading) => {
  loading(true);

  try {
    const newLabels = await store.dispatch('labels.fetch', {
      workspace: isOrganization ? 1 : 2,
      search,
    });

    if (newLabels) {
      labelsFromSearch.value = newLabels;
    }
  } catch (e) {
  } finally {
    loading(false);
  }
};
</script>

<template>
  <v-select
    v-model="model"
    multiple
    :options="labelsFromSearch"
    label="value"
    taggable
    :selectable="
      option => option.id || !labelsFromSearch.map(l => l.value.toLowerCase()).includes(option.value.toLowerCase())
    "
    :create-option="label => (label.trim() ? { value: label.trim() } : null)"
    :filterable="false"
    :placeholder="
      user.organization && user.organization.name
        ? `${$t('resumeLabelsSelect.searchIn')} ${isOrganization ? user.organization.name : $t('resumeLabelsSelect.personalGroup')}`
        : $t('resumeLabelsSelect.search')
    "
    @search="searchLabels"
  >
    <template #no-options>{{ $t('resumeLabelsSelect.typeToSearch') }}</template>

    <template #option="{ id, value }">
      <template v-if="!id">
        <div class="select-create-option">
          <PlusIcon />

          <span>{{ $t('resumeLabelsSelect.createLabel') }} "{{ value }}"</span>
        </div>
      </template>

      <template v-else>{{ value }}</template>
    </template>
  </v-select>
</template>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.v-select {
  :deep(.vs__dropdown-toggle) {
    min-height: 30px;
    width: 100%;
    border-radius: 3px;
    background: #ffffff;
    border: 1px solid #d0d0d0;
    padding: 2px 6px;
    font-size: 14px;
    box-sizing: border-box;

    &:has(:focus) {
      outline: none;
      border-color: colors.$light-blue-color;
      box-shadow:
        inset 0 1px 3px 0 colors.$light-blue-color,
        0 1px 3px 0 colors.$blue-color;
    }
  }

  :deep(.vs__dropdown-menu) {
    --vs-dropdown-max-height: 125px;

    border: var(--vs-border-width) var(--vs-border-style) var(--vs-border-color);
    top: calc(100% + 4px);
    border-radius: var(--vs-border-radius);
  }

  :deep(.vs__dropdown-option) {
    padding-left: 8px;
    padding-right: 8px;
  }

  :deep(.vs__dropdown-option--disabled) {
    display: none;
  }

  :deep(.vs__selected-options) {
    padding: 0;
    gap: 8px;
    align-items: center;
  }

  :deep(.vs__selected) {
    margin: 0;
    border: 1px solid black;
    background: white;
  }

  :deep(.vs__deselect) {
    padding: 4px 4px 4px 2px;
  }

  :deep(.vs__search) {
    margin-top: 0;
    padding: 0;
    border: none;
    font-family: Lato, sans-serif;
  }

  :deep(.vs__search::placeholder) {
    font-size: 14px;
    opacity: 0.25;
    font-style: italic;
    font-family: Lato, sans-serif;
  }

  :deep(.vs__open-indicator) {
    display: none;
  }

  :deep(.vs__actions) {
    padding-top: 0;
  }

  :deep(.vs__spinner) {
    width: 20px;
    height: 20px;
  }
}

.select-create-option {
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: colors.$light-blue-color !important;
    width: 15px;
    height: 15px;
  }
}
</style>
